import { useHead } from "@unhead/react";

import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { OrganizationStructuredData } from "@/components/helpers/seo/StructuredData";
import { PageContainer } from "@/components/pages_flexible/PageContainer";
import { Container } from "@/components/pieces/bootstrap";
import {
  LearnHeader,
  PublicLearnHeader,
} from "@/components/pieces/learn/LearnHeader";
import { PUBLIC_CONTENT_MAX_WIDTH, SITE_NAME } from "@/css/constants";
import { useLearnPageDataQuery, useLearnPageReadStatusQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

import { ArticleSeriesList } from "./components/ArticleSeriesList";
import { SeriesCard } from "./components/SeriesCard";

export function LearnPage() {
  const currentUser = useCurrentUser();
  const isLoggedIn = !!currentUser;
  const topSeriesCount = 6;

  // Main data query - runs for everyone
  const { data, loading } = useLearnPageDataQuery({
    variables: { topSeriesCount },
  });

  // Read status query - only for logged-in users
  const { data: readStatusData } = useLearnPageReadStatusQuery({
    skip: !isLoggedIn,
  });

  useHead({
    bodyAttrs: { class: "public-site" },
  });

  // Handle SSR
  if (import.meta.env.SSR && !data) {
    return null; // Apollo's getDataFromTree will re-render with data
  }

  const topSeries = data?.topArticleSeries || [];
  const otherSeries = data?.otherArticleSeries || [];
  const readStatus = readStatusData?.learnBoardReadStatus;

  // Build read status map if logged in
  const readStatusBySeriesId: Record<
    string,
    {
      completed: string[];
      inProgress: string[];
      total: number | null | undefined;
    }
  > = {};
  if (readStatus?.articleSeriesReadStatusItems) {
    ensureNonNullArray(readStatus.articleSeriesReadStatusItems).forEach(
      (item) => {
        if (item.articleSeriesId) {
          readStatusBySeriesId[item.articleSeriesId] = {
            completed: ensureNonNullArray(item.readCompletedArticleIds).map(
              String,
            ),
            inProgress: ensureNonNullArray(item.readInProgressArticleIds).map(
              String,
            ),
            total: item.totalItemCount,
          };
        }
      },
    );
  }

  return (
    <PageContainer layout="full">
      <SEOHead
        title={`${SITE_NAME} Learn: ${
          IS_EXEC ? "Leadership Guides" : "Job Search Tips and Guides"
        }`}
        description="Use our tips, guides, and tools to grow your career. Written by career experts, coaches, and talent leaders."
        canonicalPath="/learn"
        image={
          IS_EXEC
            ? "https://placement-pub.s3.us-east-2.amazonaws.com/exec-learn-meta.jpg"
            : "https://placement-pub.s3.us-east-2.amazonaws.com/learn-meta.png"
        }
      />

      <OrganizationStructuredData />

      {isLoggedIn ? <LearnHeader /> : <PublicLearnHeader />}

      <div className="py-8 bg-slate-50">
        <Container maxWidth={isLoggedIn ? undefined : PUBLIC_CONTENT_MAX_WIDTH}>
          <div className="flex flex-col gap-4">
            {/* Top/Popular Series with Preview Articles */}
            {loading ? (
              <LoadingSkeleton />
            ) : (
              <>
                {ensureNonNullArray(topSeries).map((series) => (
                  <SeriesCard
                    key={series.id}
                    series={series}
                    showPreviewArticles
                    readStatus={readStatusBySeriesId[series.id]}
                  />
                ))}

                {/* Other Series (just list, no preview articles) */}
                {otherSeries.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-2xl font-bold mb-4">More Categories</h2>
                    <ArticleSeriesList
                      series={ensureNonNullArray(otherSeries)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </div>
    </PageContainer>
  );
}

function LoadingSkeleton() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-48 bg-gray-100 mb-3 rounded-lg" />
      ))}
    </div>
  );
}
